import type { Response } from '@src/utils/request'
import { fetcher } from '@src/utils/fetcher'
import { DefaultRow } from '@components/OneTable'

/**
 * 站内信（in-site message）
 *
 * 与 /sys/notice（系统公告）的区别：
 * - 站内信需要精确到收件人，支持单发 / 全员发送 / 部门发送
 * - 每个收件人有自己的已读状态
 * - 管理员看全部；普通用户只能看到自己有收件行的消息
 *
 * 接口路径前缀从 mock 阶段的 /message/* 改为 /msg/*。
 */

// ---------- 类型 ----------

/**
 * scope 枚举：与后端保持一致（数值）
 * 1 = 全员，2 = 指定用户，3 = 指定部门
 */
export type MessageScope = 1 | 2 | 3

export const SCOPE_ALL = 1 as const
export const SCOPE_USER = 2 as const
export const SCOPE_DEPT = 3 as const

/** 列表行 VO —— 后端 MessageListVo */
export interface MessageListVo {
	id: number
	title: string
	/** 纯文本预览（后端已 strip HTML，取前 ~50 字） */
	contextPreview: string
	scope: MessageScope
	/** 后端直接给的中文 scope 标签，方便表格展示 */
	scopeLabel: string
	senderId: number
	senderName: string
	recipientCount: number
	readCount: number
	createTime: string
	/** 当前用户是否已读；管理员列表恒为 true */
	selfRead: boolean
	deptIds: number[] | null
	deptNames: string | null
}

/** 详情 VO —— 后端 MessageDetailVo */
export interface MessageDetailVo {
	id: number
	title: string
	context: string
	scope: MessageScope
	senderId: number
	senderName: string
	/** scope=2 时有值；scope=1/3 一般为 null */
	recipientIds: number[] | null
	recipientNames: string | null
	/** scope=3 时有值 */
	deptIds: number[] | null
	deptNames: string | null
	recipientCount: number
	readCount: number
	createTime: string
	createOperatorName: string
	selfRead: boolean
}

/** Header 弹层用 —— 后端 UnreadMessageVo */
export interface UnreadMessageVo {
	id: number
	title: string
	contextPreview: string
	createTime: string
}

export interface PageQuery<T> {
	dto: T
	pageNum: number
	pageSize: number
}

export interface PageResult<T> {
	list: T[]
	total: number
	current: number
	size: number
}

export interface MessageSaveDto {
	title: string
	context: string
	scope: MessageScope
	/** scope=2 时必填 */
	recipientIds?: number[]
	/** scope=3 时必填 */
	deptIds?: number[]
}

// ---------- 接口 ----------

/**
 * 站内信列表（分页）
 * 后端按当前用户角色过滤：管理员看全部，普通用户看自己有收件行的。
 */
export async function listMessages(body: PageQuery<DefaultRow>): Promise<Response<PageResult<DefaultRow>>> {
	return fetcher<Response<PageResult<DefaultRow>>>('/msg/list', 'POST', { body: body })
}

/** 详情 */
export async function getMessageById(id: number): Promise<Response<MessageDetailVo>> {
	return fetcher<Response<MessageDetailVo>>(`/msg/${id}`, 'GET')
}

/** Header 未读列表 */
export async function getUnreadList(limit = 8): Promise<Response<UnreadMessageVo[]>> {
	return fetcher<Response<UnreadMessageVo[]>>('/msg/unreadList', 'GET', {
		loading: false,
		body: { limit }
	})
}

/** 未读数（红点） */
export async function getUnreadCount(): Promise<Response<number>> {
	return fetcher<Response<number>>('/msg/unreadCount', 'GET', { loading: false })
}

/** 标记单条已读 */
export async function markAsRead(id: number): Promise<Response<null>> {
	return fetcher<Response<null>>(`/msg/read/${id}`, 'POST')
}

/** 全部已读 */
export async function markAllAsRead(): Promise<Response<null>> {
	return fetcher<Response<null>>('/msg/readAll', 'POST')
}

/** 创建站内信；后端返回新消息 id */
export async function createMessage(body: MessageSaveDto): Promise<Response<number>> {
	return fetcher<Response<number>>('/msg/save', 'POST', { body })
}

/** 删除 */
export async function deleteMessage(id: number): Promise<Response<null>> {
	return fetcher<Response<null>>(`/msg/del/${id}`, 'DELETE')
}
